import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
  forwardRef,
  useImperativeHandle,
} from 'react';
import {
  DndContext,
  DragEndEvent,
  DragStartEvent,
  useSensor,
  useSensors,
  MouseSensor,
  KeyboardSensor,
} from '@dnd-kit/core';
import { restrictToWindowEdges } from '@dnd-kit/modifiers';
import {
  Block,
  BlockType,
  ValidationResult,
  TSL_CONNECTION_RULES,
  isTerminalBlock,
  TradingPlatform,
  AssetClass,
  TimeFrame,
  QuantityType,
  CommissionType,
  StopType,
  TakeProfitType,
  ExitType,
  getDefaultExpressionData,
  BlockData,
  effectiveDisabledIds,
} from '../../types';
import StrategyBlock from './StrategyBlock';
import { v4 as uuidv4 } from 'uuid';
import {
  sanitizeBlocksForClipboard,
  materializePaste,
  isEditableTarget,
  type ClipboardPayload,
} from './blockClipboard';
import {
  applyDispatchAutoWire,
  applyDispatchAutoUnwire,
  applyExpressionOperandAutoWire,
  applyExpressionOperandAutoUnwire,
  defaultCustomBlockData,
} from './customBlockSubtype';

interface BlockCanvasProps {
  blocks: Block[];
  setBlocks: React.Dispatch<React.SetStateAction<Block[]>>;
  onSelectBlock: (block: Block) => void;
  readonly?: boolean;
  showMinimap?: boolean;
  onBlocksChange?: (blocks: Block[]) => void;
  validationResult?: ValidationResult;
}

interface CanvasState {
  scale: number;
  pan: { x: number; y: number };
  canvasSize: { width: number; height: number };
  selectedBlocks: string[];
  connecting: { blockId: string; portType: 'input' | 'output' } | null;
  mousePosition: { x: number; y: number };
  hoveredConnection: { from: string; to: string } | null;
  processingConnection: boolean;
  selectionBox: {
    start: { x: number; y: number };
    end: { x: number; y: number };
  } | null;
  dragMode: 'select' | 'pan' | 'connect';
  history: { blocks: Block[]; timestamp: number }[];
  historyIndex: number;
  toolbarPosition: { x: number; y: number };
  isDraggingToolbar: boolean;
  toolbarDragStart: { x: number; y: number; offsetX: number; offsetY: number } | null;
}

interface CanvasSettings {
  gridSize: number;
  snapToGrid: boolean;
  showGrid: boolean;
  showMinimap: boolean;
  autoSave: boolean;
  darkMode: boolean;
  animateConnections: boolean;
  showPortLabels: boolean;
  highlightDataFlow: boolean;
}

const DEFAULT_CANVAS_SETTINGS: CanvasSettings = {
  gridSize: 20,
  snapToGrid: true,
  showGrid: true,
  showMinimap: false,
  autoSave: true,
  darkMode: false,
  animateConnections: true,
  showPortLabels: false,
  highlightDataFlow: true,
};

const updateBlockMetadata = (
  block: Block,
  updates: Partial<Block['metadata']> = {}
): Block => {
  const defaultMetadata = {
    version: '1.0.0',
    created: new Date(),
    modified: new Date(),
    locked: false,
    collapsed: false,
    author: 'User',
    description: `${block.type} block`,
    tags: [],
  };

  return {
    ...block,
    metadata: {
      ...defaultMetadata,
      ...(block.metadata || {}),
      ...updates,
      modified: new Date(),
    },
  };
};

export interface BlockCanvasRef {
  autoArrangeBlocks: () => void;
  fitContent: () => void;
}

const BlockCanvas = forwardRef<BlockCanvasRef, BlockCanvasProps>(({
  blocks,
  setBlocks,
  onSelectBlock,
  readonly = false,
  showMinimap = false,
  onBlocksChange,
  validationResult,
}, ref) => {
  const canvasRef = useRef<HTMLDivElement>(null);
  // Click-drag-to-pan: dragging empty canvas moves the viewport (grab/grabbing
  // cursor). Rubber-band selection moves behind Shift+drag so nothing is lost.
  const panStart = useRef<{ mx: number; my: number; px: number; py: number } | null>(null);
  const [isPanning, setIsPanning] = useState(false);
  const [canvasState, setCanvasState] = useState<CanvasState>({
    scale: 1,
    pan: { x: 0, y: 0 },
    canvasSize: { width: 4000, height: 3000 },
    selectedBlocks: [],
    connecting: null,
    mousePosition: { x: 0, y: 0 },
    hoveredConnection: null,
    processingConnection: false,
    selectionBox: null,
    dragMode: 'select',
    history: [],
    historyIndex: -1,
    toolbarPosition: { x: 340, y: 0 },
    isDraggingToolbar: false,
    toolbarDragStart: null,
  });

  const [settings] = useState<CanvasSettings>(DEFAULT_CANVAS_SETTINGS);

  // S8g (2026-06-03) — block clipboard state. In-memory only;
  // survives within the session but not across page reloads
  // (matches the existing convention for the canvas's undo/redo
  // history). `null` ⇒ nothing has been copied yet, the Paste
  // entry / Ctrl+V is a no-op.
  const [clipboard, setClipboard] = useState<ClipboardPayload | null>(null);

  // S8g — right-click context menu. `null` ⇒ no menu showing. The
  // menu floats at screen coordinates (viewport-relative) so the
  // canvas's pan/scale transform doesn't shift it under the
  // user's cursor. `targetBlockId` is null when the menu was
  // opened on empty canvas (Paste-only menu).
  const [contextMenu, setContextMenu] = useState<{
    screenX: number;
    screenY: number;
    canvasX: number;  // canvas-space coord for paste-at-cursor
    canvasY: number;
    targetBlockId: string | null;
  } | null>(null);

  const sensors = useSensors(
    useSensor(MouseSensor, {
      activationConstraint: { distance: 10 },
    }),
    useSensor(KeyboardSensor)
  );

  // History management
  const addToHistory = useCallback((newBlocks: Block[]) => {
    setCanvasState((prev) => {
      const newHistory = prev.history.slice(0, prev.historyIndex + 1);
      newHistory.push({
        blocks: JSON.parse(JSON.stringify(newBlocks)),
        timestamp: Date.now(),
      });

      if (newHistory.length > 50) {
        newHistory.shift();
      }

      return {
        ...prev,
        history: newHistory,
        historyIndex: newHistory.length - 1,
      };
    });
  }, []);

  // Undo/Redo functionality
  const undo = useCallback(() => {
    setCanvasState((prev) => {
      if (prev.historyIndex > 0) {
        const newIndex = prev.historyIndex - 1;
        const historicalBlocks = prev.history[newIndex].blocks;
        setBlocks(historicalBlocks);
        return { ...prev, historyIndex: newIndex };
      }
      return prev;
    });
  }, [setBlocks]);

  const redo = useCallback(() => {
    setCanvasState((prev) => {
      if (prev.historyIndex < prev.history.length - 1) {
        const newIndex = prev.historyIndex + 1;
        const historicalBlocks = prev.history[newIndex].blocks;
        setBlocks(historicalBlocks);
        return { ...prev, historyIndex: newIndex };
      }
      return prev;
    });
  }, [setBlocks]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (readonly) return;

      // S8g (2026-06-03) — Pre-S8g this handler preventDefault'd
      // Ctrl+C / Ctrl+V GLOBALLY (any keystroke on the page),
      // which silently broke browser-native text copy/paste inside
      // the BlockEditor's form fields and the Custom Block code
      // editor. The fix: when the event target is an editable
      // element (input / textarea / contenteditable / inside
      // Monaco), do nothing — let the browser handle it. The
      // block-level shortcuts only run when focus is OUTSIDE an
      // editable field (i.e. the canvas itself or a non-editable
      // surface).
      if (isEditableTarget(e.target)) return;

      // Pre-emption: Ctrl+key combos that have block-level
      // semantics. NOTE Backspace deliberately NOT in this list —
      // some browsers map it to history-back outside an editable,
      // and we already gate the back action by selectedBlocks
      // count below.
      if (
        (e.ctrlKey || e.metaKey) &&
        ['z', 'y', 'a', 'c', 'v', 'x', 'd'].includes(e.key.toLowerCase())
      ) {
        e.preventDefault();
      }

      if (e.key === 'Escape') {
        setCanvasState((prev) => ({
          ...prev,
          connecting: null,
          selectionBox: null,
        }));
        setContextMenu(null);  // S8g — Escape also dismisses the menu
      } else if (
        (e.ctrlKey || e.metaKey) &&
        e.key.toLowerCase() === 'z' &&
        !e.shiftKey
      ) {
        undo();
      } else if (
        (e.ctrlKey || e.metaKey) &&
        (e.key.toLowerCase() === 'y' ||
          (e.key.toLowerCase() === 'z' && e.shiftKey))
      ) {
        redo();
      } else if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'a') {
        setCanvasState((prev) => ({
          ...prev,
          selectedBlocks: blocks.map((b) => b.id),
        }));
      } else if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'd') {
        handleDuplicateSelected();
      } else if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'c') {
        // S8g — Copy selected blocks to the in-memory clipboard.
        handleCopySelected();
      } else if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'x') {
        // S8g — Cut: copy + delete in one step.
        handleCutSelected();
      } else if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'v') {
        // S8g — Paste at the default offset (+20,+20 from source).
        handlePaste();
      } else if (e.key === 'Delete' || e.key === 'Backspace') {
        handleDeleteSelected();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
    // S8g (2026-06-03) — `clipboard` added so the keydown re-
    // registers when the user copies something; otherwise the
    // listener closure would reference the STALE `handlePaste`
    // (whose clipboard dep was null at registration time) and
    // Ctrl+V would no-op after the first copy. The Copy/Cut
    // handlers' deps (readonly / blocks / selectedBlocks) are
    // already in this list. We deliberately do NOT add the
    // handler functions themselves — they're declared lexically
    // BELOW this useEffect and listing them would TDZ-throw on
    // first render. Lazy lookup at fire-time is fine because
    // event handlers run after all consts have initialized.
  }, [readonly, blocks, canvasState.selectedBlocks, undo, redo, clipboard]);

  // Auto-save functionality
  useEffect(() => {
    if (settings.autoSave && onBlocksChange) {
      const timeoutId = setTimeout(() => {
        onBlocksChange(blocks);
      }, 1000);
      return () => clearTimeout(timeoutId);
    }
  }, [blocks, settings.autoSave, onBlocksChange]);

  // Dynamic canvas sizing
  useEffect(() => {
    if (blocks.length > 0) {
      const padding = 500;
      const maxX = Math.max(
        ...blocks.map(
          (b) =>
            b.position.x + (b.type === BlockType.STRATEGY_CONFIG ? 300 : 200)
        )
      );
      const maxY = Math.max(...blocks.map((b) => b.position.y + 120));

      setCanvasState((prev) => ({
        ...prev,
        canvasSize: {
          width: Math.max(4000, maxX + padding),
          height: Math.max(3000, maxY + padding),
        },
      }));
    }
  }, [blocks]);

  // TSL-compliant block creation with validation
  const createNewBlock = useCallback(
    (blockType: BlockType, e: React.DragEvent) => {
      if (readonly) return;

      const canvasRect = canvasRef.current?.getBoundingClientRect();
      if (!canvasRect) return;

      const x =
        (e.clientX - canvasRect.left - canvasState.pan.x) / canvasState.scale;
      const y =
        (e.clientY - canvasRect.top - canvasState.pan.y) / canvasState.scale;

      // Snap to grid if enabled
      const finalX = settings.snapToGrid
        ? Math.round(x / settings.gridSize) * settings.gridSize
        : x;
      const finalY = settings.snapToGrid
        ? Math.round(y / settings.gridSize) * settings.gridSize
        : y;

      const newBlock: Block = {
        id: uuidv4(),
        type: blockType,
        position: { x: finalX, y: finalY },
        data: getDefaultBlockData(blockType) as BlockData,
        connections: { inputs: [], outputs: [] },
        metadata: {
          version: '1.0.0',
          created: new Date(),
          modified: new Date(),
          locked: false,
          collapsed: false,
          author: 'User',
          description: `TSL ${blockType} block`,
          tags: ['tsl'],
        },
      };

      const newBlocks = [...blocks, newBlock];
      setBlocks(newBlocks);
      addToHistory(newBlocks);

      // Auto-select the new block
      setCanvasState((prev) => ({ ...prev, selectedBlocks: [newBlock.id] }));
      onSelectBlock(newBlock);
    },
    [
      blocks,
      canvasState.pan,
      canvasState.scale,
      settings,
      readonly,
      setBlocks,
      addToHistory,
      onSelectBlock,
    ]
  );

  // TSL connection validation
  const isValidConnection = useCallback(
    (sourceType: BlockType, targetType: BlockType): boolean => {
      const allowedTargets = TSL_CONNECTION_RULES[sourceType];
      return allowedTargets?.includes(targetType) || false;
    },
    []
  );

  const hasCircularDependency = useCallback(
    (sourceId: string, targetId: string): boolean => {
      const visited = new Set<string>();
      let visitCount = 0;
      const maxVisits = blocks.length * 2;

      const dfs = (currentId: string): boolean => {
        if (visitCount++ > maxVisits) return true;
        if (currentId === sourceId) return true;
        if (visited.has(currentId)) return false;
        visited.add(currentId);

        const block = blocks.find((b) => b.id === currentId);
        if (!block?.connections?.outputs) return false;

        return block.connections.outputs.some(dfs);
      };

      return dfs(targetId);
    },
    [blocks]
  );

  const startConnection = useCallback(
    (blockId: string, portType: 'input' | 'output') => {
      if (readonly) return;

      const block = blocks.find((b) => b.id === blockId);
      if (!block || block.data.locked) return;
      if (block.type === BlockType.STRATEGY_CONFIG) return;
      if (portType === 'output' && isTerminalBlock(block.type)) return;

      setCanvasState((prev) => ({
        ...prev,
        connecting: { blockId, portType },
      }));
    },
    [blocks, readonly]
  );

  const completeConnection = useCallback(
    (blockId: string, portType: 'input' | 'output') => {
      if (
        !canvasState.connecting ||
        canvasState.processingConnection ||
        readonly
      )
        return;

      setCanvasState((prev) => ({ ...prev, processingConnection: true }));

      try {
        if (
          canvasState.connecting.blockId === blockId ||
          canvasState.connecting.portType === portType
        ) {
          return;
        }

        const sourceId =
          canvasState.connecting.portType === 'output'
            ? canvasState.connecting.blockId
            : blockId;
        const targetId =
          canvasState.connecting.portType === 'output'
            ? blockId
            : canvasState.connecting.blockId;

        const sourceBlock = blocks.find((b) => b.id === sourceId);
        const targetBlock = blocks.find((b) => b.id === targetId);

        if (!sourceBlock || !targetBlock) return;

        // TSL validation checks
        if (!isValidConnection(sourceBlock.type, targetBlock.type)) {
          console.warn(
            `TSL: Invalid connection from ${sourceBlock.type} to ${targetBlock.type}`
          );
          return;
        }

        if (
          sourceBlock.connections.outputs.includes(targetId) ||
          targetBlock.connections.inputs.includes(sourceId)
        )
          return;
        if (hasCircularDependency(sourceId, targetId)) return;

        // TSL: Check for multiple PRICE_DATA connections to same target
        if (sourceBlock.type === BlockType.PRICE_DATA) {
          const existingPriceDataConnection =
            targetBlock.connections.inputs.some((inputId) => {
              const inputBlock = blocks.find((b) => b.id === inputId);
              return inputBlock?.type === BlockType.PRICE_DATA;
            });
          if (existingPriceDataConnection) {
            console.warn(
              'TSL: Only one PRICE_DATA block can connect to each destination'
            );
            return;
          }
        }

        // S8j (2026-06-03) — Canvas-edge auto-wiring for per-trade
        // dispatch. When the source is a CUSTOM block with a per-
        // trade subtype (qty_calculator / sl_calculator /
        // tp_calculator) AND the target is an ENTRY, also write
        // the corresponding *CalculatorBlockId field on the entry.
        // Pre-S8j this was only settable via JSON migration, which
        // made manual authoring of per-trade dispatch unreachable.
        // applyDispatchAutoWire is a no-op when either side
        // doesn't match (per-bar source, non-ENTRY target,
        // risk_rule, etc.) — so the existing edge-creation
        // behaviour for all other block combinations is unchanged.
        const newBlocks = blocks.map((block) => {
          if (block.id === sourceId) {
            return updateBlockMetadata({
              ...block,
              connections: {
                ...block.connections,
                outputs: [...block.connections.outputs, targetId],
              },
            });
          }
          if (block.id === targetId) {
            const withInput = {
              ...block,
              connections: {
                ...block.connections,
                inputs: [...block.connections.inputs, sourceId],
              },
            };
            // S8j auto-wire for per-trade calc dispatch.
            // S10c auto-wire for Expression operand pointers.
            // Both are no-ops when the source/target don't match
            // the pattern, so they compose cleanly.
            const wired1 = applyDispatchAutoWire(sourceBlock, withInput);
            const wired2 = applyExpressionOperandAutoWire(sourceBlock, wired1);
            return updateBlockMetadata(wired2);
          }
          return block;
        });

        setBlocks(newBlocks);
        addToHistory(newBlocks);
      } finally {
        setCanvasState((prev) => ({
          ...prev,
          connecting: null,
          processingConnection: false,
        }));
      }
    },
    [
      canvasState.connecting,
      canvasState.processingConnection,
      readonly,
      blocks,
      isValidConnection,
      hasCircularDependency,
      setBlocks,
      addToHistory,
    ]
  );

  // Enhanced selection management
  const handleBlockSelect = useCallback(
    (blockId: string, event: React.MouseEvent) => {
      const block = blocks.find((b) => b.id === blockId);
      if (!block) return;

      if (event.ctrlKey || event.metaKey) {
        setCanvasState((prev) => ({
          ...prev,
          selectedBlocks: prev.selectedBlocks.includes(blockId)
            ? prev.selectedBlocks.filter((id) => id !== blockId)
            : [...prev.selectedBlocks, blockId],
        }));
      } else {
        setCanvasState((prev) => ({ ...prev, selectedBlocks: [blockId] }));
      }

      onSelectBlock(block);
    },
    [blocks, onSelectBlock]
  );

  // Bulk operations
  const handleDuplicateSelected = useCallback(() => {
    if (readonly || canvasState.selectedBlocks.length === 0) return;

    const selectedBlocksData = blocks.filter((b) =>
      canvasState.selectedBlocks.includes(b.id)
    );
    const newBlocks = [...blocks];
    const newIds: string[] = [];

    selectedBlocksData.forEach((block) => {
      // TSL: Don't duplicate STRATEGY_CONFIG blocks
      if (block.type === BlockType.STRATEGY_CONFIG) return;

      const newBlock: Block = updateBlockMetadata(
        {
          ...JSON.parse(JSON.stringify(block)),
          id: uuidv4(),
          position: {
            x: block.position.x + 20,
            y: block.position.y + 20,
          },
          connections: { inputs: [], outputs: [] },
        },
        {
          created: new Date(),
          locked: false,
        }
      );

      newBlocks.push(newBlock);
      newIds.push(newBlock.id);
    });

    setBlocks(newBlocks);
    addToHistory(newBlocks);
    setCanvasState((prev) => ({ ...prev, selectedBlocks: newIds }));
  }, [readonly, canvasState.selectedBlocks, blocks, setBlocks, addToHistory]);

  const handleDeleteSelected = useCallback(() => {
    if (readonly || canvasState.selectedBlocks.length === 0) return;

    const newBlocks = blocks
      .filter((block) => !canvasState.selectedBlocks.includes(block.id))
      .map((block) =>
        updateBlockMetadata({
          ...block,
          connections: {
            inputs: block.connections.inputs.filter(
              (id) => !canvasState.selectedBlocks.includes(id)
            ),
            outputs: block.connections.outputs.filter(
              (id) => !canvasState.selectedBlocks.includes(id)
            ),
          },
        })
      );

    setBlocks(newBlocks);
    addToHistory(newBlocks);
    setCanvasState((prev) => ({ ...prev, selectedBlocks: [] }));
  }, [readonly, canvasState.selectedBlocks, blocks, setBlocks, addToHistory]);

  // ── S8g (2026-06-03) — Block clipboard: copy / cut / paste ────────
  //
  // Operates on canvasState.selectedBlocks (single OR multi-select).
  // Skips STRATEGY_CONFIG (it's a singleton — same policy as
  // handleDuplicateSelected). Intra-clipboard references (graph
  // edges + data.*BlockId runtime refs) get remapped to fresh
  // UUIDs on paste; external refs are stripped at sanitize time.
  //
  // Paste position: when the caller supplies an explicit canvas
  // position (e.g. from a context-menu click) the bounding-box
  // top-left of the pasted group lands at that position. With no
  // position, the pasted group sits at +20,+20 from its source
  // (matches handleDuplicateSelected). Multi-block paste preserves
  // the RELATIVE positions of the source blocks.

  const handleCopySelected = useCallback(() => {
    if (readonly || canvasState.selectedBlocks.length === 0) return;
    const payload = sanitizeBlocksForClipboard(blocks, canvasState.selectedBlocks);
    if (payload) setClipboard(payload);
  }, [readonly, canvasState.selectedBlocks, blocks]);

  const handleCutSelected = useCallback(() => {
    if (readonly || canvasState.selectedBlocks.length === 0) return;
    const payload = sanitizeBlocksForClipboard(blocks, canvasState.selectedBlocks);
    if (!payload) return;
    setClipboard(payload);
    // Delegate the deletion to the existing handler so connection
    // cleanup + history + selection clearing stay consistent.
    handleDeleteSelected();
  }, [readonly, canvasState.selectedBlocks, blocks, handleDeleteSelected]);

  const handlePaste = useCallback(
    (options?: { canvasX: number; canvasY: number }) => {
      if (readonly || !clipboard) return;
      // Default offset (+20,+20) when no explicit position is given.
      // When a position IS given (e.g. right-click → Paste at cursor),
      // compute the offset that lands the snapshot's TOP-LEFT at
      // that point so multi-block paste preserves relative layout.
      let offset = { x: 20, y: 20 };
      if (options) {
        const minX = Math.min(...clipboard.blocks.map(b => b.position?.x ?? 0));
        const minY = Math.min(...clipboard.blocks.map(b => b.position?.y ?? 0));
        offset = { x: options.canvasX - minX, y: options.canvasY - minY };
      }
      const pasted = materializePaste(clipboard, { newId: uuidv4, offset });
      // Wrap each pasted block in updateBlockMetadata so the
      // canvas's metadata invariants (created/modified Dates etc.)
      // hold. materializePaste already sets fresh Date objects but
      // updateBlockMetadata fills in any other defaults the
      // codebase expects.
      const newBlocks = [...blocks, ...pasted.map(b => updateBlockMetadata(b))];
      setBlocks(newBlocks);
      addToHistory(newBlocks);
      // Select the pasted blocks so the user can immediately drag
      // / nudge / re-paste them.
      setCanvasState((prev) => ({
        ...prev,
        selectedBlocks: pasted.map(b => b.id),
      }));
    },
    [readonly, clipboard, blocks, setBlocks, addToHistory]
  );

  // Zoom and pan controls
  const handleZoomIn = useCallback(() => {
    setCanvasState((prev) => ({
      ...prev,
      scale: Math.min(prev.scale + 0.1, 3),
    }));
  }, []);

  const handleZoomOut = useCallback(() => {
    setCanvasState((prev) => ({
      ...prev,
      scale: Math.max(prev.scale - 0.1, 0.3),
    }));
  }, []);

  const handleResetView = useCallback(() => {
    setCanvasState((prev) => ({ ...prev, scale: 1, pan: { x: 0, y: 0 } }));
  }, []);

  const handleFitContent = useCallback(() => {
    if (blocks.length === 0) return;

    const padding = 100;
    const minX = Math.min(...blocks.map((b) => b.position.x)) - padding;
    const minY = Math.min(...blocks.map((b) => b.position.y)) - padding;
    const maxX =
      Math.max(
        ...blocks.map(
          (b) =>
            b.position.x + (b.type === BlockType.STRATEGY_CONFIG ? 300 : 200)
        )
      ) + padding;
    const maxY = Math.max(...blocks.map((b) => b.position.y + 120)) + padding;

    const canvasWidth = canvasRef.current?.clientWidth || 1000;
    const canvasHeight = canvasRef.current?.clientHeight || 800;

    const scaleX = canvasWidth / (maxX - minX);
    const scaleY = canvasHeight / (maxY - minY);
    const newScale = Math.min(Math.max(0.3, Math.min(scaleX, scaleY)), 2);

    const centerX = (minX + maxX) / 2;
    const centerY = (minY + maxY) / 2;

    const newPanX = canvasWidth / 2 - centerX * newScale;
    const newPanY = canvasHeight / 2 - centerY * newScale;

    setCanvasState((prev) => ({
      ...prev,
      scale: newScale,
      pan: { x: newPanX, y: newPanY },
    }));
  }, [blocks]);

  // TSL-compliant auto-arrange with Strategy Config positioning
  const autoArrangeBlocks = useCallback(() => {
    if (blocks.length === 0) return;

    const arrangedBlocks = [...blocks];
    const horizontalSpacing = 380;
    const verticalSpacing = 180;
    const startX = 50;
    const startY = 50;

    // TSL: Handle Strategy Config block specially (top-left, isolated)
    const strategyConfigBlock = arrangedBlocks.find(
      (block) => block.type === BlockType.STRATEGY_CONFIG
    );

    if (strategyConfigBlock) {
      const strategyIndex = arrangedBlocks.findIndex(
        (b) => b.id === strategyConfigBlock.id
      );
      if (strategyIndex !== -1) {
        arrangedBlocks[strategyIndex] = updateBlockMetadata({
          ...arrangedBlocks[strategyIndex],
          position: {
            x: startX,
            y: startY,
          },
        });
      }
    }

    // TSL: Find root blocks (excluding Strategy Config, PRICE_DATA, or blocks with no inputs)
    const rootBlocks = arrangedBlocks.filter(
      (block) =>
        block.type !== BlockType.STRATEGY_CONFIG &&
        (!block.connections.inputs || block.connections.inputs.length === 0)
    );

    if (rootBlocks.length === 0) {
      rootBlocks.push(
        ...arrangedBlocks.filter(
          (block) => block.type !== BlockType.STRATEGY_CONFIG
        )
      );
    }

    // Topological sort for TSL-compliant arrangement
    const visited = new Set<string>();
    const layers: string[][] = [];

    const dfs = (blockId: string, layer: number) => {
      if (visited.has(blockId)) return;
      visited.add(blockId);

      if (!layers[layer]) layers[layer] = [];
      layers[layer].push(blockId);

      const block = arrangedBlocks.find((b) => b.id === blockId);
      if (block?.connections.outputs) {
        block.connections.outputs.forEach((outputId) => {
          const outputBlock = arrangedBlocks.find((b) => b.id === outputId);
          if (outputBlock?.type !== BlockType.STRATEGY_CONFIG) {
            dfs(outputId, layer + 1);
          }
        });
      }
    };

    rootBlocks.forEach((rootBlock) => {
      if (rootBlock.type !== BlockType.STRATEGY_CONFIG) {
        dfs(rootBlock.id, 0);
      }
    });

    // Position blocks by layers (excluding Strategy Config)
    const strategyConfigOffset = strategyConfigBlock ? 250 : 0;

    layers.forEach((layer, layerIndex) => {
      layer.forEach((blockId, blockIndex) => {
        const block = arrangedBlocks.find((b) => b.id === blockId);
        if (block?.type === BlockType.STRATEGY_CONFIG) return;

        const blockIndex2 = arrangedBlocks.findIndex((b) => b.id === blockId);
        if (blockIndex2 !== -1) {
          arrangedBlocks[blockIndex2] = updateBlockMetadata({
            ...arrangedBlocks[blockIndex2],
            position: {
              x: startX + layerIndex * horizontalSpacing,
              y: startY + strategyConfigOffset + blockIndex * verticalSpacing,
            },
          });
        }
      });
    });

    setBlocks(arrangedBlocks);
    addToHistory(arrangedBlocks);
  }, [blocks, setBlocks, addToHistory]);

  // Connection helper functions
  const deleteConnection = useCallback(
    (sourceId: string, targetId: string) => {
      if (readonly) return;

      // S8j (2026-06-03) — Resolve the source block ONCE before
      // the map so we can pass it to applyDispatchAutoUnwire on the
      // target branch. The helper clears the entry's
      // *CalculatorBlockId field IF AND ONLY IF that field
      // currently points at this exact source id — so removing an
      // older "shadowed" edge doesn't clobber the live pointer.
      const sourceBlock = blocks.find((b) => b.id === sourceId);

      const newBlocks = blocks.map((block) => {
        if (block.id === sourceId) {
          return updateBlockMetadata({
            ...block,
            connections: {
              ...block.connections,
              outputs: block.connections.outputs.filter(
                (id) => id !== targetId
              ),
            },
          });
        }
        if (block.id === targetId) {
          const withoutInput = {
            ...block,
            connections: {
              ...block.connections,
              inputs: block.connections.inputs.filter((id) => id !== sourceId),
            },
          };
          // S8j + S10c — symmetric auto-unwire. Each helper clears
          // whichever pointer slot was set by its wire counterpart;
          // a no-op when nothing matches.
          let unwired = withoutInput;
          if (sourceBlock) {
            unwired = applyDispatchAutoUnwire(sourceBlock, unwired);
            unwired = applyExpressionOperandAutoUnwire(sourceBlock, unwired);
          }
          return updateBlockMetadata(unwired);
        }
        return block;
      });

      setBlocks(newBlocks);
      addToHistory(newBlocks);
      setCanvasState((prev) => ({ ...prev, hoveredConnection: null }));
    },
    [readonly, blocks, setBlocks, addToHistory]
  );

  // TSL-compliant default block data
  const getDefaultBlockData = useCallback(
    (type: BlockType) => {
      const strategyConfig = blocks.find(
        (b) => b.type === BlockType.STRATEGY_CONFIG
      )?.data;

      const baseDefaults: Record<BlockType, Partial<BlockData>> = {
        [BlockType.STRATEGY_CONFIG]: {
          name: 'Strategy Configuration',
          title: 'My TSL Trading Strategy',
          shortTitle: '',
          overlay: true,
          pyramiding: 1,
          maxPositions: 1,
          default_qty_type: QuantityType.FIXED,
          default_qty_value: 1,
          initial_capital: 10000,
          // S4e (2026-06-02) — `currency: 'USD'` removed (dead display-only
          // field; engine doesn't price by currency). See audit doc.
          slippage: 1,
          commission_type: CommissionType.PERCENTAGE,
          commission_value: 0.1,
          calc_on_order_fills: true,
          calc_on_every_tick: false,
          process_orders_on_close: true,
          maxDrawdown: 20,
          // S4e (2026-06-02) — `maxRiskPerTrade: 2` removed (dead field;
          // engine reads entry-level `risk_per_trade` + propFirm-level
          // `maxRiskPerTradePct` — separate, working surfaces). See audit doc.
          precision: 2,
          platform: TradingPlatform.TRADINGVIEW,
          assetClass: AssetClass.FOREX,
          timeframe: TimeFrame.H1,
        },
        [BlockType.PRICE_DATA]: {
          name: 'Price Data',
          // S4f (2026-06-02): `symbol: 'EURUSD'` removed (dead UI field;
          // backtest uses run-time selected symbol). See audit doc.
          timeframe: TimeFrame.H1,
          source: 'close',
          shift: 0,
          // S4a (2026-06-02) — `lookback` removed (dead UI field; engine
          // always uses the full backtest range). See
          // docs/BLOCK_SETTINGS_AUDIT.md.
          description: 'Custom price data source for TSL strategy components',
        },
        [BlockType.ENTRY]: {
          name: 'Entry Signal',
          entryType: 'market',
          direction: 'long',
          conditions: {},
          logic: 'AND',
          quantity_type: strategyConfig?.default_qty_type || QuantityType.FIXED,
          quantity_value: strategyConfig?.default_qty_value || 1,
          useStrategyDefaults: true,
        },
        [BlockType.EXIT]: {
          name: 'Exit Signal',
          exitType: 'market',
          condition: '',
          logic: 'AND',
          exitMode: ExitType.FULL,
          partialQuantity: 50,
        },
        [BlockType.STOP_LOSS]: {
          name: 'Stop Loss',
          stopType: StopType.FIXED_POINTS,
          stopValue: 50,
          activationPrice: 0,
          limitOffset: 1,
          timeDuration: 60,
          timeUnit: 'minutes',
          volatilityMultiplier: 2,
          exitMode: ExitType.FULL,
          partialQuantity: 50,
        },
        [BlockType.TAKE_PROFIT]: {
          name: 'Take Profit',
          tpType: TakeProfitType.FIXED_POINTS,
          tpValue: 100,
          exitMode: ExitType.FULL,
          partialQuantity: 50,
        },
        [BlockType.MOVING_AVERAGE]: {
          name: 'Moving Average',
          indicator: 'sma',
          period: 20,
          source: 'close',
          smoothing: 2,
          timeOffset: 0,
        },
        [BlockType.MOMENTUM_OSCILLATOR]: {
          name: 'Momentum/Oscillator',
          indicator: 'rsi',
          period: 14,
          overbought: 70,
          oversold: 30,
          source: 'close',
          fastPeriod: 12,
          slowPeriod: 26,
          signalPeriod: 9,
          kSmooth: 1,
          dPeriod: 3,
          period1: 7,
          period2: 14,
          period3: 28,
        },
        [BlockType.VOLATILITY]: {
          name: 'Volatility Indicator',
          indicator: 'atr',
          period: 14,
          smoothing: 2,
          multiplier: 2,
          stdDev: 2,
        },
        [BlockType.VOLUME]: {
          name: 'Volume Indicator',
          indicator: 'volume_sma',
          period: 20,
          session: 'daily',
          standardDeviations: 0,
        },
        [BlockType.TREND]: {
          name: 'Trend Indicator',
          indicator: 'adx',
          period: 14,
          threshold: 25,
          accelerationFactor: 0.02,
          maxAcceleration: 0.2,
          tenkanPeriod: 9,
          kijunPeriod: 26,
          senkouBPeriod: 52,
        },
        [BlockType.CUSTOM]: {
          // TSL migration: new custom blocks are safe-TSL from the first
          // keystroke (tslText + tslAst) — never host JS, which the engine
          // quarantines and would make the block a silent no-op.
          ...defaultCustomBlockData(),
          description: 'Safe-TSL custom block for indicators, patterns, analysis, filters, or signal generators',
          // S4d (2026-06-02): `outputTypes: ['value']` removed (dead field). See audit doc.
        },
        [BlockType.SETUP_EVENT]: {
          name: 'Setup Event',
          description: 'Objective market-structure event (P9.1 setup language) — normally emitted by the setup compiler, not hand-placed.',
        },
        [BlockType.SETUP_STATE]: {
          name: 'Setup State Machine',
          description: 'Deterministic scenario state machine (P9.1) — emitted by the setup compiler with a serialized machine definition.',
        },
        [BlockType.TIME_FILTER]: {
          // S8c (2026-06-03) — F5 fix mirror. The engine reads
          // `data.sessions` as a boolean record; pre-S8c this
          // default seeded `sessionFilter.sessions` (array) and
          // `sessions: {}` (empty), making the UI-visible
          // European/American defaults invisible to the engine.
          // Now writes the engine-canonical shape directly.
          // See BlockEditor.tsx TIME_FILTER default + audit doc
          // S8c section.
          name: 'Time Filter',
          filterType: 'session',
          sessions: { european: true, american: true },
          tradingDays: {},
          startTime: '09:00',
          endTime: '17:00',
          timezone: 'UTC',
        },
        [BlockType.LOGIC_GATE]: {
          name: 'Logic Gate',
          type: 'AND',
          invert: false,
          description: 'Combine multiple signals',
          // S4a (2026-06-02) — `threshold` and `fuzzyLogic` removed (dead
          // UI fields; src/engine/graph.ts:evalLogicGate only dispatches on
          // `type` + `invert`). See docs/BLOCK_SETTINGS_AUDIT.md.
        },
        [BlockType.EXPRESSION]: getDefaultExpressionData(),
        // TSL Pattern Recognition blocks (3 separate types)
        [BlockType.CHART_PATTERNS]: {
          name: 'Chart Patterns',
          patternType: 'chart_pattern',
          // S6b (2026-06-02) — `trendline_break` removed (engine doesn't
          // implement it; see chartPatterns.ts header). Replaced with
          // head_shoulders which IS implemented.
          chartPatterns: ['support_resistance_break', 'head_shoulders'],
          sensitivity: 'medium',
          confirmationBars: 1,
          minPatternSize: 5,
        },
        [BlockType.CANDLESTICK_PATTERNS]: {
          name: 'Candlestick Patterns',
          patternType: 'candlestick',
          candlestickPatterns: ['doji', 'hammer'],
          sensitivity: 'medium',
          confirmationBars: 1,
        },
      };

      return baseDefaults[type] || { name: 'TSL Block' };
    },
    [blocks]
  );

  // Block operations
  const handleDeleteBlock = useCallback(
    (blockId: string) => {
      if (readonly) return;

      const newBlocks = blocks
        .filter((block) => block.id !== blockId)
        .map((block) =>
          updateBlockMetadata({
            ...block,
            connections: {
              inputs: block.connections.inputs.filter((id) => id !== blockId),
              outputs: block.connections.outputs.filter((id) => id !== blockId),
            },
          })
        );

      setBlocks(newBlocks);
      addToHistory(newBlocks);

      setCanvasState((prev) => ({
        ...prev,
        selectedBlocks: prev.selectedBlocks.filter((id) => id !== blockId),
      }));
    },
    [readonly, blocks, setBlocks, addToHistory]
  );

  const handleDuplicateBlock = useCallback(
    (block: Block) => {
      if (readonly || block.data.locked) return;
      if (block.type === BlockType.STRATEGY_CONFIG) return; // TSL: Don't duplicate strategy config

      const newBlock: Block = updateBlockMetadata(
        {
          ...JSON.parse(JSON.stringify(block)),
          id: uuidv4(),
          position: {
            x:
              block.position.x + (settings.snapToGrid ? settings.gridSize : 20),
            y:
              block.position.y + (settings.snapToGrid ? settings.gridSize : 20),
          },
          connections: { inputs: [], outputs: [] },
        },
        {
          created: new Date(),
          locked: false,
        }
      );

      const newBlocks = [...blocks, newBlock];
      setBlocks(newBlocks);
      addToHistory(newBlocks);
    },
    [
      readonly,
      blocks,
      setBlocks,
      addToHistory,
      settings.snapToGrid,
      settings.gridSize,
    ]
  );

  const handleToggleLock = useCallback(
    (blockId: string) => {
      if (readonly) return;

      const newBlocks = blocks.map((block) => {
        if (block.id === blockId) {
          return updateBlockMetadata({
            ...block,
            data: { ...block.data, locked: !block.data.locked },
          });
        }
        return block;
      });

      setBlocks(newBlocks);
      addToHistory(newBlocks);
    },
    [readonly, blocks, setBlocks, addToHistory]
  );

  // S91 — Disable toggle. Non-destructive: a disabled block stays on the canvas
  // (dimmed) but is excluded from the backtest engine, the Pine/Python
  // converters, and validation (each strips it via isBlockDisabled). Ideal for
  // ablation — toggle a filter off, re-run, toggle it back on if it didn't
  // help. Mirrors handleToggleLock; stored in data.disabled like data.locked.
  const handleToggleDisable = useCallback(
    (blockId: string) => {
      if (readonly) return;
      const newBlocks = blocks.map((block) => {
        if (block.id === blockId) {
          return updateBlockMetadata({
            ...block,
            data: { ...block.data, disabled: !block.data.disabled },
          });
        }
        return block;
      });
      setBlocks(newBlocks);
      addToHistory(newBlocks);
    },
    [readonly, blocks, setBlocks, addToHistory]
  );

  // Selection box handling
  const handleSelectionStart = useCallback(
    (e: React.MouseEvent) => {
      if (e.button !== 0 || readonly) return;

      const rect = canvasRef.current?.getBoundingClientRect();
      if (!rect) return;

      const startX =
        (e.clientX - rect.left - canvasState.pan.x) / canvasState.scale;
      const startY =
        (e.clientY - rect.top - canvasState.pan.y) / canvasState.scale;

      setCanvasState((prev) => ({
        ...prev,
        selectionBox: {
          start: { x: startX, y: startY },
          end: { x: startX, y: startY },
        },
      }));
    },
    [readonly, canvasState.pan, canvasState.scale]
  );

  const handleSelectionMove = useCallback(
    (e: React.MouseEvent) => {
      if (!canvasState.selectionBox || readonly) return;

      const rect = canvasRef.current?.getBoundingClientRect();
      if (!rect) return;

      const endX =
        (e.clientX - rect.left - canvasState.pan.x) / canvasState.scale;
      const endY =
        (e.clientY - rect.top - canvasState.pan.y) / canvasState.scale;

      setCanvasState((prev) => ({
        ...prev,
        selectionBox: prev.selectionBox
          ? {
              ...prev.selectionBox,
              end: { x: endX, y: endY },
            }
          : null,
      }));
    },
    [canvasState.selectionBox, canvasState.pan, canvasState.scale, readonly]
  );

  const handleSelectionEnd = useCallback(() => {
    if (!canvasState.selectionBox || readonly) return;

    const { start, end } = canvasState.selectionBox;
    const minX = Math.min(start.x, end.x);
    const maxX = Math.max(start.x, end.x);
    const minY = Math.min(start.y, end.y);
    const maxY = Math.max(start.y, end.y);

    const selectedBlockIds = blocks
      .filter((block) => {
        const blockRight =
          block.position.x +
          (block.type === BlockType.STRATEGY_CONFIG ? 300 : 200);
        const blockBottom = block.position.y + 120;

        return (
          block.position.x < maxX &&
          blockRight > minX &&
          block.position.y < maxY &&
          blockBottom > minY
        );
      })
      .map((block) => block.id);

    setCanvasState((prev) => ({
      ...prev,
      selectedBlocks: selectedBlockIds,
      selectionBox: null,
    }));
  }, [canvasState.selectionBox, blocks, readonly]);

  // Render selection box
  const renderSelectionBox = useCallback(() => {
    if (!canvasState.selectionBox) return null;

    const { start, end } = canvasState.selectionBox;
    const x = Math.min(start.x, end.x);
    const y = Math.min(start.y, end.y);
    const width = Math.abs(end.x - start.x);
    const height = Math.abs(end.y - start.y);

    return (
      <div
        className="absolute border-2 border-blue-500 bg-blue-100 bg-opacity-20 pointer-events-none"
        style={{
          left: x,
          top: y,
          width,
          height,
        }}
      />
    );
  }, [canvasState.selectionBox]);

  // Enhanced mouse handlers
  const handleCanvasMouseDown = useCallback(
    (e: React.MouseEvent) => {
      // Only react to the empty canvas background (clicking a block must not
      // pan/select). Shift+drag = rubber-band select; plain drag = pan.
      if (e.button !== 0) return;
      const target = e.target as HTMLElement | null;
      if (!target) return;
      // Reject clicks that landed on a block or any interactive descendant —
      // those have their own handlers.
      if (target.closest('[data-block-type]')) return;
      if (target.closest('button, input, select, textarea, a, [role="button"]')) return;
      // Accept the canvas root itself OR the inner transformed background layer.
      const isCanvasBg =
        target === canvasRef.current ||
        target.dataset?.canvasBg === 'true' ||
        !!target.closest('[data-canvas-bg="true"]');
      if (!isCanvasBg) return;
      if (e.shiftKey) {
        handleSelectionStart(e);
      } else {
        panStart.current = {
          mx: e.clientX,
          my: e.clientY,
          px: canvasState.pan.x,
          py: canvasState.pan.y,
        };
        setIsPanning(true);
      }
    },
    [handleSelectionStart, canvasState.pan.x, canvasState.pan.y]
  );

  const handleCanvasMouseMove = useCallback(
    (e: React.MouseEvent) => {
      handleMouseMove(e);
      if (panStart.current) {
        const dx = e.clientX - panStart.current.mx;
        const dy = e.clientY - panStart.current.my;
        const px = panStart.current.px + dx;
        const py = panStart.current.py + dy;
        setCanvasState((prev) => ({ ...prev, pan: { x: px, y: py } }));
      } else if (canvasState.selectionBox) {
        handleSelectionMove(e);
      }
    },
    [canvasState.selectionBox]
  );

  const endCanvasInteraction = useCallback(() => {
    if (panStart.current) {
      panStart.current = null;
      setIsPanning(false);
    }
    if (canvasState.selectionBox) {
      handleSelectionEnd();
    }
  }, [canvasState.selectionBox, handleSelectionEnd]);

  const handleCanvasMouseUp = endCanvasInteraction;

  // TSL-enhanced connection rendering
  const renderConnections = useCallback(() => {
    const connections: JSX.Element[] = [];

    blocks.forEach((sourceBlock) => {
      if (!sourceBlock.connections?.outputs) return;

      sourceBlock.connections.outputs.forEach((targetId) => {
        const targetBlock = blocks.find((block) => block.id === targetId);
        if (!targetBlock) return;

        // Calculate exact port positions based on block dimensions and port positioning
        const blockWidth = 300; // All blocks are now 300px wide
        const portSize = 16; // w-4 h-4 = 16px
        const portOffsetY = 24; // top-6 = 24px from top
        const portOffsetX = 8; // -left-2/-right-2 = 8px from edge

        // Output port position (right side of source block)
        // Center of the port: block.right + portOffsetX + portSize/2
        const sourceX = sourceBlock.position.x + blockWidth + portOffsetX + portSize / 2;
        const sourceY = sourceBlock.position.y + portOffsetY + portSize / 2;

        // Input port position (left side of target block)
        // Center of the port: block.left - portOffsetX + portSize/2
        // The port is positioned -left-2, so it's 8px to the left of the block edge
        const targetX = targetBlock.position.x - portOffsetX + portSize / 2;
        const targetY = targetBlock.position.y + portOffsetY + portSize / 2;

        const distance = Math.sqrt(
          Math.pow(targetX - sourceX, 2) + Math.pow(targetY - sourceY, 2)
        );
        const controlPointDistance = Math.max(
          50,
          Math.min(200, distance * 0.4)
        );

        const path = `M ${sourceX} ${sourceY} C ${
          sourceX + controlPointDistance
        } ${sourceY}, ${
          targetX - controlPointDistance
        } ${targetY}, ${targetX} ${targetY}`;

        const isHovered =
          canvasState.hoveredConnection?.from === sourceBlock.id &&
          canvasState.hoveredConnection?.to === targetId;
        const isSelected =
          canvasState.selectedBlocks.includes(sourceBlock.id) ||
          canvasState.selectedBlocks.includes(targetId);
        const hasError = validationResult?.errors.some(
          (e) => e.blockId === sourceBlock.id || e.blockId === targetId
        );

        let strokeColor = '#3B82F6';
        if (hasError) strokeColor = '#EF4444'; // red
        else if (isHovered) strokeColor = '#F59E0B'; // amber
        else if (isSelected) strokeColor = '#8B5CF6'; // purple

        // TSL: Special styling for pattern recognition connections
        const isPatternConnection = [
          BlockType.CHART_PATTERNS,
          BlockType.CANDLESTICK_PATTERNS,
        ].includes(sourceBlock.type);

        connections.push(
          <g key={`${sourceBlock.id}-${targetId}`}>
            <path
              d={path}
              stroke={strokeColor}
              strokeWidth={isSelected ? '3' : isHovered ? '3' : '2'}
              strokeDasharray={isPatternConnection ? '5,5' : 'none'}
              fill="none"
              className={`transition-all duration-200 ${
                settings.animateConnections ? 'animate-pulse' : ''
              }`}
              onMouseEnter={() =>
                setCanvasState((prev) => ({
                  ...prev,
                  hoveredConnection: { from: sourceBlock.id, to: targetId },
                }))
              }
              onMouseLeave={() =>
                setCanvasState((prev) => ({
                  ...prev,
                  hoveredConnection: null,
                }))
              }
              onClick={() =>
                !readonly &&
                isHovered &&
                deleteConnection(sourceBlock.id, targetId)
              }
              style={{
                cursor: isHovered && !readonly ? 'pointer' : 'default',
                filter: isSelected
                  ? 'drop-shadow(0 0 4px rgba(139, 92, 246, 0.5))'
                  : 'none',
              }}
            />

            {settings.highlightDataFlow && isSelected && (
              <circle r="4" fill={strokeColor}>
                <animateMotion dur="2s" repeatCount="indefinite" path={path} />
              </circle>
            )}

            {isHovered && !readonly && (
              <circle
                cx={(sourceX + targetX) / 2}
                cy={(sourceY + targetY) / 2}
                r="10"
                fill="#EF4444"
                className="cursor-pointer"
                onClick={(e) => {
                  e.stopPropagation();
                  deleteConnection(sourceBlock.id, targetId);
                }}
              />
            )}

            {settings.showPortLabels && (
              <text
                x={(sourceX + targetX) / 2}
                y={(sourceY + targetY) / 2 - 15}
                textAnchor="middle"
                className="text-xs fill-gray-600 pointer-events-none"
              >
                {isPatternConnection ? 'pattern' : 'signal'}
              </text>
            )}
          </g>
        );
      });
    });

    return (
      <svg
        className="absolute top-0 left-0 w-full h-full pointer-events-none"
        style={{ zIndex: 1 }}
      >
        <g className="pointer-events-auto">{connections}</g>
      </svg>
    );
  }, [
    blocks,
    canvasState.hoveredConnection,
    canvasState.selectedBlocks,
    settings,
    validationResult,
    readonly,
    deleteConnection,
  ]);

  // Connection preview
  const renderConnectionPreview = useCallback(() => {
    if (!canvasState.connecting) return null;

    const connecting = canvasState.connecting;
    const block = blocks.find((b) => b.id === connecting.blockId);
    if (!block) return null;

    const blockWidth = 300;
    const portSize = 16;
    const portOffsetY = 24;
    const portOffsetX = 8;

    const startX =
      connecting.portType === 'output'
        ? block.position.x + blockWidth + portOffsetX + portSize / 2
        : block.position.x - portOffsetX + portSize / 2;
    const startY = block.position.y + portOffsetY + portSize / 2;

    const distance = Math.sqrt(
      Math.pow(canvasState.mousePosition.x - startX, 2) +
        Math.pow(canvasState.mousePosition.y - startY, 2)
    );
    const controlPointDistance = Math.max(50, Math.min(200, distance * 0.4));

    const path =
      connecting.portType === 'output'
        ? `M ${startX} ${startY} C ${
            startX + controlPointDistance
          } ${startY}, ${canvasState.mousePosition.x - controlPointDistance} ${
            canvasState.mousePosition.y
          }, ${canvasState.mousePosition.x} ${canvasState.mousePosition.y}`
        : `M ${startX} ${startY} C ${
            startX - controlPointDistance
          } ${startY}, ${canvasState.mousePosition.x + controlPointDistance} ${
            canvasState.mousePosition.y
          }, ${canvasState.mousePosition.x} ${canvasState.mousePosition.y}`;

    return (
      <svg
        className="absolute top-0 left-0 w-full h-full pointer-events-none"
        style={{ zIndex: 10 }}
      >
        <path
          d={path}
          stroke="#3B82F6"
          strokeWidth="2"
          fill="none"
          strokeDasharray="5,5"
          className="animate-pulse"
        />
      </svg>
    );
  }, [canvasState.connecting, canvasState.mousePosition, blocks]);

  // Grid rendering
  const renderGrid = useCallback(() => {
    if (!settings.showGrid) return null;

    const gridLines: JSX.Element[] = [];
    const { width, height } = canvasState.canvasSize;
    const { gridSize } = settings;

    for (let x = 0; x <= width; x += gridSize) {
      gridLines.push(
        <line
          key={`v-${x}`}
          x1={x}
          y1={0}
          x2={x}
          y2={height}
          stroke={settings.darkMode ? '#374151' : '#F3F4F6'}
          strokeWidth="1"
        />
      );
    }

    for (let y = 0; y <= height; y += gridSize) {
      gridLines.push(
        <line
          key={`h-${y}`}
          x1={0}
          y1={y}
          x2={width}
          y2={y}
          stroke={settings.darkMode ? '#374151' : '#F3F4F6'}
          strokeWidth="1"
        />
      );
    }

    return (
      <svg
        className="absolute top-0 left-0 w-full h-full pointer-events-none"
        style={{ zIndex: 0 }}
      >
        {gridLines}
      </svg>
    );
  }, [
    settings.showGrid,
    settings.darkMode,
    settings.gridSize,
    canvasState.canvasSize,
  ]);

  // TSL-enhanced minimap
  const renderMinimap = useCallback(() => {
    if (!showMinimap && !settings.showMinimap) return null;

    const minimapScale = 0.1;
    const minimapWidth = canvasState.canvasSize.width * minimapScale;
    const minimapHeight = canvasState.canvasSize.height * minimapScale;

    return (
      <div className="absolute bottom-4 right-4 bg-white border border-gray-300 rounded-lg shadow-lg p-2">
        <div className="text-xs text-gray-600 mb-1">TSL Strategy Overview</div>
        <div
          className="relative bg-gray-100 border"
          style={{ width: minimapWidth, height: minimapHeight }}
        >
          {blocks.map((block) => {
            let blockColor = 'bg-gray-400';
            if (block.type === BlockType.STRATEGY_CONFIG)
              blockColor = 'bg-cyan-400';
            else if (isTerminalBlock(block.type)) blockColor = 'bg-green-400';
            else if (
              [
                BlockType.CHART_PATTERNS,
                BlockType.CANDLESTICK_PATTERNS,
              ].includes(block.type)
            )
              blockColor = 'bg-pink-400';

            return (
              <div
                key={block.id}
                className={`absolute rounded ${
                  canvasState.selectedBlocks.includes(block.id)
                    ? 'bg-blue-500'
                    : blockColor
                }`}
                style={{
                  left: block.position.x * minimapScale,
                  top: block.position.y * minimapScale,
                  width:
                    (block.type === BlockType.STRATEGY_CONFIG ? 300 : 200) *
                    minimapScale,
                  height: 120 * minimapScale,
                }}
              />
            );
          })}

          <div
            className="absolute border-2 border-blue-500 bg-blue-100 bg-opacity-30"
            style={{
              left: -canvasState.pan.x * minimapScale,
              top: -canvasState.pan.y * minimapScale,
              width: (canvasRef.current?.clientWidth || 800) * minimapScale,
              height: (canvasRef.current?.clientHeight || 600) * minimapScale,
            }}
          />
        </div>
      </div>
    );
  }, [showMinimap, settings.showMinimap, canvasState, blocks]);

  // Event handlers
  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      if (readonly) return;

      const blockType =
        e.dataTransfer.getData('application/x-block-type') ||
        e.dataTransfer.getData('text/plain');

      if (
        !blockType ||
        !Object.values(BlockType).includes(blockType as BlockType)
      ) {
        return;
      }

      createNewBlock(blockType as BlockType, e);
    },
    [readonly, createNewBlock]
  );

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
  }, []);

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    if (canvasRef.current) {
      const rect = canvasRef.current.getBoundingClientRect();
      setCanvasState((prev) => ({
        ...prev,
        mousePosition: {
          x: (e.clientX - rect.left - prev.pan.x) / prev.scale,
          y: (e.clientY - rect.top - prev.pan.y) / prev.scale,
        },
      }));
    }
  }, []);

  const handleContextMenu = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      if (readonly) return;
      setCanvasState((prev) => ({ ...prev, connecting: null }));

      // S8g (2026-06-03) — Right-click opens a block-clipboard menu.
      // Resolve the click target: was a block clicked, or empty
      // canvas? We walk up from the event target looking for a
      // [data-block-id] attribute that StrategyBlock renders on
      // its outer wrapper. If found, the menu's targetBlockId is
      // that block; if the block isn't already in the multi-
      // selection, replace the selection so Copy/Cut operates on
      // the right-clicked block (standard editor convention).
      const el = (e.target as HTMLElement | null)?.closest?.('[data-block-id]');
      const targetBlockId = el ? el.getAttribute('data-block-id') : null;
      if (targetBlockId && !canvasState.selectedBlocks.includes(targetBlockId)) {
        setCanvasState((prev) => ({ ...prev, selectedBlocks: [targetBlockId] }));
      }

      // Convert click position from viewport coords to canvas-
      // space coords so paste-at-cursor lands at the right spot
      // when pan/zoom are non-default. The canvas DOM applies a
      // `transform: translate(pan) scale(scale)` so the math is
      // (viewport - canvasRect.origin - pan) / scale.
      const rect = canvasRef.current?.getBoundingClientRect();
      const scale = canvasState.scale || 1;
      const canvasX = rect
        ? (e.clientX - rect.left - canvasState.pan.x) / scale
        : 0;
      const canvasY = rect
        ? (e.clientY - rect.top - canvasState.pan.y) / scale
        : 0;
      setContextMenu({
        screenX: e.clientX,
        screenY: e.clientY,
        canvasX,
        canvasY,
        targetBlockId,
      });
    },
    [readonly, canvasState.selectedBlocks, canvasState.scale, canvasState.pan]
  );

  // S8g — dismiss the context menu on any outside-click. Bound to
  // window so a click on the canvas, the toolbar, or anywhere
  // else closes it. The menu itself stops-propagation so clicking
  // an item doesn't dismiss before the action runs.
  useEffect(() => {
    if (!contextMenu) return;
    const onClickAway = () => setContextMenu(null);
    // setTimeout-0 so the same click that OPENED the menu doesn't
    // immediately close it.
    const id = setTimeout(() => {
      window.addEventListener('click', onClickAway);
    }, 0);
    return () => {
      clearTimeout(id);
      window.removeEventListener('click', onClickAway);
    };
  }, [contextMenu]);

  const handleWheel = useCallback((e: WheelEvent) => {
    if (e.ctrlKey || e.metaKey) {
      e.preventDefault();
      // Two-finger trackpad pinch (and ctrl/cmd + wheel) zooms. Anchor the zoom on the
      // CURSOR: keep the world point under the pointer fixed on screen as scale changes.
      // The canvas DOM applies `transform: translate(pan) scale(scale)`, so a world point w
      // sits at screen-local `pan + w*scale`; solving for the pre-zoom world point and
      // re-pinning it after the scale change gives the new pan below.
      const rect = canvasRef.current?.getBoundingClientRect();
      const cursorX = rect ? e.clientX - rect.left : 0;
      const cursorY = rect ? e.clientY - rect.top : 0;
      const delta = e.deltaY > 0 ? -0.05 : 0.05;
      setCanvasState((prev) => {
        const newScale = Math.min(Math.max(0.3, prev.scale + delta), 3);
        if (!rect || newScale === prev.scale) {
          return { ...prev, scale: newScale };
        }
        const worldX = (cursorX - prev.pan.x) / prev.scale;
        const worldY = (cursorY - prev.pan.y) / prev.scale;
        return {
          ...prev,
          scale: newScale,
          pan: { x: cursorX - worldX * newScale, y: cursorY - worldY * newScale },
        };
      });
    } else if (e.shiftKey) {
      e.preventDefault();
      setCanvasState((prev) => ({
        ...prev,
        pan: { ...prev.pan, x: prev.pan.x - e.deltaY },
      }));
    } else {
      const rect = canvasRef.current?.getBoundingClientRect();
      if (rect) {
        const isInsideCanvas =
          e.clientX >= rect.left &&
          e.clientX <= rect.right &&
          e.clientY >= rect.top &&
          e.clientY <= rect.bottom;

        if (isInsideCanvas) {
          e.preventDefault();
          // Two-finger trackpad scroll pans BOTH axes: vertical (deltaY) AND
          // horizontal (deltaX). A mouse wheel reports deltaX:0 so its vertical-
          // only behaviour is unchanged; a precision touchpad's left/right swipe
          // now pans the canvas horizontally. (Shift+scroll still maps deltaY→x.)
          setCanvasState((prev) => ({
            ...prev,
            pan: { x: prev.pan.x - e.deltaX, y: prev.pan.y - e.deltaY },
          }));
        }
      }
    }
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    canvas.addEventListener('wheel', handleWheel, { passive: false });

    return () => {
      canvas.removeEventListener('wheel', handleWheel);
    };
  }, [handleWheel]);

  // Drag handling for blocks
  const handleDragStart = useCallback(
    (event: DragStartEvent) => {
      const { active } = event;
      if (!canvasState.selectedBlocks.includes(active.id as string)) {
        setCanvasState((prev) => ({
          ...prev,
          selectedBlocks: [active.id as string],
        }));
      }
    },
    [canvasState.selectedBlocks]
  );

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { delta } = event;

      const newBlocks = blocks.map((block) => {
        if (canvasState.selectedBlocks.includes(block.id)) {
          const newX = block.position.x + delta.x / canvasState.scale;
          const newY = block.position.y + delta.y / canvasState.scale;

          return updateBlockMetadata({
            ...block,
            position: {
              x: settings.snapToGrid
                ? Math.round(newX / settings.gridSize) * settings.gridSize
                : newX,
              y: settings.snapToGrid
                ? Math.round(newY / settings.gridSize) * settings.gridSize
                : newY,
            },
          });
        }
        return block;
      });

      setBlocks(newBlocks);
      addToHistory(newBlocks);
    },
    [
      canvasState.selectedBlocks,
      canvasState.scale,
      settings.snapToGrid,
      settings.gridSize,
      blocks,
      setBlocks,
      addToHistory,
    ]
  );

  // S92 — effective disabled closure (explicitly-disabled blocks + the
  // conditions built on them). Used to dim the WHOLE filter chain, not just the
  // toggled block, so disabling a source visibly cascades to its consumers.
  const effectivelyDisabledSet = useMemo(() => effectiveDisabledIds(blocks), [blocks]);

  // Canvas transform style
  const canvasTransform = useMemo(
    () => ({
      transform: `scale(${canvasState.scale}) translate(${
        canvasState.pan.x / canvasState.scale
      }px, ${canvasState.pan.y / canvasState.scale}px)`,
      transformOrigin: '0 0',
      width: `${canvasState.canvasSize.width}px`,
      height: `${canvasState.canvasSize.height}px`,
    }),
    [canvasState]
  );

  // Toolbar drag functionality
  const handleToolbarDragStart = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Calculate the offset between mouse position and toolbar's current position
    // Since toolbar uses right-based positioning, we need to calculate the offset differently
    const toolbarElement = e.currentTarget.closest('.toolbar-container') as HTMLElement;
    if (!toolbarElement) return;
    
    // Calculate offset from the toolbar's top-right corner (since we use right positioning)
    const offsetX = (window.innerWidth - e.clientX) - canvasState.toolbarPosition.x;
    const offsetY = e.clientY - canvasState.toolbarPosition.y;
    
    setCanvasState(prev => ({
      ...prev,
      isDraggingToolbar: true,
      toolbarDragStart: { 
        x: e.clientX, 
        y: e.clientY,
        offsetX,
        offsetY
      }
    }));
  }, [canvasState.toolbarPosition]);

  const handleToolbarDragMove = useCallback((e: React.MouseEvent) => {
    if (!canvasState.isDraggingToolbar || !canvasState.toolbarDragStart) return;
    
    e.preventDefault();
    e.stopPropagation();
    
    // Calculate new position based on mouse position minus the initial offset
    const newRight = (window.innerWidth - e.clientX) - canvasState.toolbarDragStart.offsetX;
    const newTop = e.clientY - canvasState.toolbarDragStart.offsetY;
    
    setCanvasState(prev => ({
      ...prev,
      toolbarPosition: {
        x: newRight,
        y: newTop
      }
    }));
  }, [canvasState.isDraggingToolbar, canvasState.toolbarDragStart]);

  const handleToolbarDragEnd = useCallback(() => {
    setCanvasState(prev => ({
      ...prev,
      isDraggingToolbar: false,
      toolbarDragStart: null
    }));
  }, []);

  // Global mouse event handlers for toolbar dragging
  useEffect(() => {
    const handleGlobalMouseMove = (e: MouseEvent) => {
      if (canvasState.isDraggingToolbar) {
        // Convert MouseEvent to React.MouseEvent-like object
        const reactEvent = {
          clientX: e.clientX,
          clientY: e.clientY,
          preventDefault: () => e.preventDefault(),
          stopPropagation: () => e.stopPropagation(),
        } as React.MouseEvent;
        
        handleToolbarDragMove(reactEvent);
      }
    };

    const handleGlobalMouseUp = () => {
      if (canvasState.isDraggingToolbar) {
        handleToolbarDragEnd();
      }
    };

    if (canvasState.isDraggingToolbar) {
      document.addEventListener('mousemove', handleGlobalMouseMove);
      document.addEventListener('mouseup', handleGlobalMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleGlobalMouseMove);
      document.removeEventListener('mouseup', handleGlobalMouseUp);
    };
  }, [canvasState.isDraggingToolbar, handleToolbarDragMove, handleToolbarDragEnd]);

  // Expose methods through ref
  useImperativeHandle(ref, () => ({
    autoArrangeBlocks,
    fitContent: handleFitContent,
  }), [autoArrangeBlocks, handleFitContent]);

  return (
    <DndContext
      sensors={sensors}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      modifiers={[restrictToWindowEdges]}
    >
      <div
        className={`flex-1 relative overflow-hidden h-full flex flex-col ${
          settings.darkMode ? 'bg-gray-900' : 'bg-gray-50'
        }`}
      >
        {/* Enhanced TSL Toolbar with Drag Handle */}
        <div
          className={`toolbar-container absolute z-20 flex space-x-2 p-2 rounded-lg border transition-shadow ${
            settings.darkMode
              ? 'bg-gray-800 border-gray-700 text-gray-200'
              : 'bg-white border-slate-200 text-slate-700'
          } ${
            canvasState.isDraggingToolbar ? 'shadow-2xl' : 'shadow-md'
          }`}
          style={{
            top: `${canvasState.toolbarPosition.y}px`,
            right: `${canvasState.toolbarPosition.x}px`,
            cursor: canvasState.isDraggingToolbar ? 'grabbing' : 'default',
            userSelect: 'none',
          }}
        >
          {/* Drag Handle - First button on the left */}
          <div className="flex space-x-1 border-r border-gray-200 pr-2">
            <button
              onMouseDown={handleToolbarDragStart}
              className={`p-2 rounded transition-colors ${
                canvasState.isDraggingToolbar 
                  ? 'bg-blue-100 dark:bg-blue-800 text-blue-600 dark:text-blue-400' 
                  : 'hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-400'
              }`}
              title="Drag to reposition toolbar"
              style={{ cursor: 'grab' }}
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <circle cx="9" cy="5" r="1"></circle>
                <circle cx="9" cy="12" r="1"></circle>
                <circle cx="9" cy="19" r="1"></circle>
                <circle cx="15" cy="5" r="1"></circle>
                <circle cx="15" cy="12" r="1"></circle>
                <circle cx="15" cy="19" r="1"></circle>
              </svg>
            </button>
          </div>

          <div className="flex space-x-1 border-r border-gray-200 pr-2">
            <button
              onClick={handleZoomIn}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
              title="Zoom In (Ctrl + Scroll)"
              disabled={canvasState.scale >= 3}
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <circle cx="11" cy="11" r="8"></circle>
                <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                <line x1="11" y1="8" x2="11" y2="14"></line>
                <line x1="8" y1="11" x2="14" y2="11"></line>
              </svg>
            </button>

            <button
              onClick={handleZoomOut}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
              title="Zoom Out (Ctrl + Scroll)"
              disabled={canvasState.scale <= 0.3}
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <circle cx="11" cy="11" r="8"></circle>
                <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                <line x1="8" y1="11" x2="14" y2="11"></line>
              </svg>
            </button>

            <span className="px-2 py-1 text-xs bg-gray-100 dark:bg-gray-700 rounded self-center">
              {Math.round(canvasState.scale * 100)}%
            </span>
          </div>

          <div className="flex space-x-1 border-r border-gray-200 pr-2">
            <button
              onClick={handleResetView}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
              title="Reset View"
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"></path>
                <path d="M3 3v5h5"></path>
              </svg>
            </button>

            <button
              onClick={handleFitContent}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
              title="Fit All TSL Blocks"
              disabled={blocks.length === 0}
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path d="M15 3h6v6"></path>
                <path d="M9 21H3v-6"></path>
                <path d="M21 3l-7 7"></path>
                <path d="M3 21l7-7"></path>
              </svg>
            </button>
          </div>

          <div className="flex space-x-1">
            <button
              onClick={autoArrangeBlocks}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
              title="Auto Arrange TSL Blocks"
              disabled={blocks.length === 0 || readonly}
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                <line x1="3" y1="9" x2="21" y2="9"></line>
                <line x1="9" y1="21" x2="9" y2="9"></line>
              </svg>
            </button>

            <button
              onClick={undo}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
              title="Undo (Ctrl+Z)"
              disabled={readonly || canvasState.historyIndex <= 0}
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <polyline points="1,4 1,10 7,10"></polyline>
                <path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10"></path>
              </svg>
            </button>

            <button
              onClick={redo}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
              title="Redo (Ctrl+Y)"
              disabled={
                readonly ||
                canvasState.historyIndex >= canvasState.history.length - 1
              }
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <polyline points="23,4 23,10 17,10"></polyline>
                <path d="M20.49 15a9 9 0 1 1-2.13-9.36L23 10"></path>
              </svg>
            </button>
          </div>
        </div>

        {/* Canvas */}
        <div
          ref={canvasRef}
          data-testid="builder-canvas-viewport"
          className={`flex-1 relative overflow-hidden ${
            isPanning ? 'cursor-grabbing' : 'cursor-grab'
          }`}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onMouseDown={handleCanvasMouseDown}
          onMouseMove={handleCanvasMouseMove}
          onMouseUp={handleCanvasMouseUp}
          onMouseLeave={endCanvasInteraction}
          onContextMenu={handleContextMenu}
        >
          <div
            className="absolute inset-0"
            style={canvasTransform}
            data-canvas-bg="true"
          >
            {renderGrid()}
            {renderSelectionBox()}

            {blocks.map((block) => (
              <StrategyBlock
                key={block.id}
                block={block}
                isSelected={canvasState.selectedBlocks.includes(block.id)}
                onSelect={(e) => handleBlockSelect(block.id, e)}
                onStartConnection={startConnection}
                onCompleteConnection={completeConnection}
                onDelete={handleDeleteBlock}
                onDuplicate={handleDuplicateBlock}
                onToggleLock={handleToggleLock}
                onToggleDisable={handleToggleDisable}
                isEffectivelyDisabled={effectivelyDisabledSet.has(block.id)}
                connectingPortType={canvasState.connecting?.portType}
                readonly={readonly}
                validationErrors={validationResult?.errors.filter(
                  (e) => e.blockId === block.id
                )}
                validationWarnings={validationResult?.warnings.filter(
                  (w) => w.blockId === block.id
                )}
              />
            ))}

            {renderConnections()}
            {renderConnectionPreview()}
          </div>
        </div>

        {renderMinimap()}

        {/*
          S8g (2026-06-03) — Block clipboard context menu. Rendered at
          screen-fixed coordinates (NOT inside the pan/zoom canvas
          transform) so it lands exactly where the user clicked.
          Items dispatch the same handlers as the Ctrl+C/X/V hotkeys.
          The menu auto-dismisses on outside-click via the useEffect
          window listener above; stopPropagation on the menu's own
          onClick keeps an item-click from being treated as outside.
        */}
        {contextMenu && !readonly && (
          <ContextMenu
            menu={contextMenu}
            hasSelection={canvasState.selectedBlocks.length > 0}
            hasClipboard={clipboard != null}
            onCopy={() => { handleCopySelected(); setContextMenu(null); }}
            onCut={() => { handleCutSelected(); setContextMenu(null); }}
            onPaste={() => {
              handlePaste({ canvasX: contextMenu.canvasX, canvasY: contextMenu.canvasY });
              setContextMenu(null);
            }}
            onDuplicate={() => { handleDuplicateSelected(); setContextMenu(null); }}
            onDelete={() => { handleDeleteSelected(); setContextMenu(null); }}
          />
        )}
      </div>
    </DndContext>
  );
});

// ── S8g (2026-06-03) — Context-menu component ──────────────────────────
//
// Floating menu fixed to viewport coords. Items disable themselves
// when the relevant precondition is missing (e.g. Paste is grey
// when the clipboard is empty; Copy/Cut are grey when nothing is
// selected). Keeps the menu honest about what each click will do.

interface ContextMenuProps {
  menu: { screenX: number; screenY: number; targetBlockId: string | null };
  hasSelection: boolean;
  hasClipboard: boolean;
  onCopy: () => void;
  onCut: () => void;
  onPaste: () => void;
  onDuplicate: () => void;
  onDelete: () => void;
}

const ContextMenu: React.FC<ContextMenuProps> = ({
  menu,
  hasSelection,
  hasClipboard,
  onCopy,
  onCut,
  onPaste,
  onDuplicate,
  onDelete,
}) => {
  const platformMod = typeof navigator !== 'undefined' && /Mac/.test(navigator.platform)
    ? '⌘'
    : 'Ctrl';
  return (
    <div
      className="fixed z-[60] min-w-[180px] rounded-md border border-gray-200 bg-white shadow-lg py-1 text-sm"
      style={{ left: menu.screenX, top: menu.screenY }}
      onClick={(e) => e.stopPropagation()}
      role="menu"
    >
      <MenuItem label="Copy" shortcut={`${platformMod}+C`} disabled={!hasSelection} onClick={onCopy} />
      <MenuItem label="Cut" shortcut={`${platformMod}+X`} disabled={!hasSelection} onClick={onCut} />
      <MenuItem label="Paste" shortcut={`${platformMod}+V`} disabled={!hasClipboard} onClick={onPaste} />
      <div className="my-1 border-t border-gray-100" />
      <MenuItem label="Duplicate" shortcut={`${platformMod}+D`} disabled={!hasSelection} onClick={onDuplicate} />
      <MenuItem label="Delete" shortcut="Del" disabled={!hasSelection} onClick={onDelete} danger />
    </div>
  );
};

interface MenuItemProps {
  label: string;
  shortcut?: string;
  disabled?: boolean;
  danger?: boolean;
  onClick: () => void;
}

const MenuItem: React.FC<MenuItemProps> = ({ label, shortcut, disabled, danger, onClick }) => (
  <button
    type="button"
    role="menuitem"
    onClick={disabled ? undefined : onClick}
    disabled={disabled}
    className={`
      w-full flex items-center justify-between px-3 py-1.5 text-left
      ${disabled ? 'text-gray-400 cursor-not-allowed' : danger ? 'text-red-600 hover:bg-red-50' : 'text-gray-700 hover:bg-gray-100'}
    `}
  >
    <span>{label}</span>
    {shortcut && <span className="ml-4 text-xs text-gray-400 font-mono">{shortcut}</span>}
  </button>
);

export default BlockCanvas;

